(() => {
  const previousRuntime = window.__aoHeaderRuntime;

  if (previousRuntime && typeof previousRuntime.destroy === 'function') {
    previousRuntime.destroy();
  }

  const HEADER_SELECTOR = '[data-ao2-header]';
  const ANNOUNCEMENT_SELECTOR = '[data-ao-announcement]';
  const SMART_HEADER_CLASS = 'ao-smart-header-hidden';
  const ANNOUNCEMENT_HIDDEN_CLASS = 'ao-announcement-scrolled-away';
  const SMART_HEADER_READY_CLASS = 'ao-smart-header-ready';
  let announcementHeightCache = 0;
  const FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const getMatches = (scope, selector) => {
    const matches = [];

    if (scope instanceof Element && scope.matches(selector)) {
      matches.push(scope);
    }

    if (typeof scope.querySelectorAll === 'function') {
      matches.push(...scope.querySelectorAll(selector));
    }

    return matches;
  };

  class AnnouncementRotator {
    constructor(root) {
      this.root = root;
      this.items = [...root.querySelectorAll('[data-ao-announcement-item]')];
      this.toggleButton = root.querySelector('[data-ao-announcement-toggle]');
      this.currentIndex = Math.max(
        0,
        this.items.findIndex((item) => item.classList.contains('is-active')),
      );
      this.autoplay = root.dataset.autoplay === 'true';
      this.speed = Math.min(10000, Math.max(3000, Number(root.dataset.speed) || 5000));
      this.timer = null;
      this.destroyed = false;
      this.pointerInside = false;
      this.focusInside = false;
      this.editorPaused = false;
      this.userPaused = false;
      this.listeners = [];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleToggleClick = this.handleToggleClick.bind(this);

      try {
        this.userPaused = window.sessionStorage.getItem('ao-announcement-paused') === 'true';
      } catch (error) {
        this.userPaused = false;
      }

      this.listen(document, 'visibilitychange', this.handleVisibilityChange);
      this.listen(root, 'pointerenter', () => {
        this.pointerInside = true;
        this.stop();
        document.dispatchEvent(new CustomEvent('ao:header:reveal'));
      });
      this.listen(root, 'pointerleave', () => {
        this.pointerInside = false;
        this.schedule();
      });
      this.listen(root, 'focusin', () => {
        this.focusInside = true;
        this.stop();
        document.dispatchEvent(new CustomEvent('ao:header:reveal'));
      });
      this.listen(root, 'focusout', () => {
        requestAnimationFrame(() => {
          if (this.destroyed) return;

          this.focusInside = this.root.contains(document.activeElement);
          this.schedule();
        });
      });
      this.listen(this.toggleButton, 'click', this.handleToggleClick);

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.handleMotionChange);
      } else {
        this.reducedMotion.addListener(this.handleMotionChange);
      }

      this.setIndex(this.currentIndex);
      this.updateToggleButton();
      this.schedule();
    }

    listen(target, eventName, handler, options) {
      if (!target) return;

      target.addEventListener(eventName, handler, options);
      this.listeners.push(() => target.removeEventListener(eventName, handler, options));
    }

    setIndex(nextIndex) {
      if (!this.items.length) return;

      this.currentIndex = (nextIndex + this.items.length) % this.items.length;

      this.items.forEach((item, index) => {
        const isActive = index === this.currentIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (item.matches('a[href]')) {
          if (isActive) {
            item.removeAttribute('tabindex');
          } else {
            item.setAttribute('tabindex', '-1');
          }
        }
      });
    }

    canRotate() {
      const focusBlocksRotation =
        this.focusInside && document.activeElement !== this.toggleButton;

      return (
        this.autoplay &&
        this.items.length > 1 &&
        !this.reducedMotion.matches &&
        !document.hidden &&
        !this.pointerInside &&
        !focusBlocksRotation &&
        !this.editorPaused &&
        !this.userPaused
      );
    }

    schedule() {
      this.stop();

      if (this.destroyed || !this.canRotate()) return;

      this.timer = window.setTimeout(() => {
        this.setIndex(this.currentIndex + 1);
        this.schedule();
      }, this.speed);
    }

    stop() {
      if (this.timer !== null) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    handleVisibilityChange() {
      this.schedule();
    }

    handleMotionChange() {
      if (this.reducedMotion.matches) {
        this.setIndex(0);
      }

      this.updateToggleButton();
      this.schedule();
    }

    handleToggleClick() {
      this.userPaused = !this.userPaused;

      try {
        window.sessionStorage.setItem('ao-announcement-paused', String(this.userPaused));
      } catch (error) {
        // Rotation still works when storage is unavailable.
      }

      this.updateToggleButton();
      this.schedule();
      document.dispatchEvent(new CustomEvent('ao:header:reveal'));
    }

    updateToggleButton() {
      if (!this.toggleButton) return;

      const paused = this.userPaused || this.reducedMotion.matches;
      const label = paused
        ? this.toggleButton.dataset.resumeLabel
        : this.toggleButton.dataset.pauseLabel;

      this.toggleButton.classList.toggle('is-paused', paused);
      this.toggleButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      this.toggleButton.setAttribute('aria-label', label || '');
      this.toggleButton.title = label || '';
    }

    selectBlock(blockId, selected) {
      const selectedIndex = this.items.findIndex((item) => item.dataset.blockId === blockId);

      if (selected && selectedIndex !== -1) {
        this.editorPaused = true;
        this.stop();
        this.setIndex(selectedIndex);
      } else if (!selected) {
        this.editorPaused = false;
        this.schedule();
      }
    }

    destroy() {
      this.destroyed = true;
      this.stop();
      this.listeners.forEach((removeListener) => removeListener());

      if (typeof this.reducedMotion.removeEventListener === 'function') {
        this.reducedMotion.removeEventListener('change', this.handleMotionChange);
      } else {
        this.reducedMotion.removeListener(this.handleMotionChange);
      }
    }
  }

  class SmartHeader {
    constructor(root) {
      this.root = root;
      this.drawer = root.querySelector('[data-ao2-drawer]');
      this.openButton = root.querySelector('[data-ao2-menu-open]');
      this.closeButtons = [...root.querySelectorAll('[data-ao2-menu-close]')];
      this.details = [...root.querySelectorAll('[data-ao2-details]')];
      this.lastScrollY = Math.max(window.scrollY || 0, 0);
      this.lastMovementAt = 0;
      this.direction = 0;
      this.directionTravel = 0;
      this.interactionUntil = 0;
      this.hidden = false;
      this.frame = null;
      this.previousFocus = null;
      this.drawerScrollY = null;
      this.drawerOpenFrame = null;
      this.drawerCloseTimer = null;
      this.drawerTransitionTarget = null;
      this.drawerTransitionHandler = null;
      this.pendingDrawerRestoreFocus = false;
      this.destroyed = false;
      this.listeners = [];
      this.cartDrawerObserver = null;
      this.mobileViewport = window.matchMedia('(max-width: 900px)');

      this.topZone = 8;
      this.hideStart = 8;
      this.downTolerance = 8;
      this.upTolerance = 18;
      this.noiseTolerance = 2;
      this.directionWindow = 220;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);

      this.bindEvents();
      this.observeCartDrawer();
      this.restoreVisibility();
    }

    listen(target, eventName, handler, options) {
      if (!target) return;

      target.addEventListener(eventName, handler, options);
      this.listeners.push(() => target.removeEventListener(eventName, handler, options));
    }

    bindEvents() {
      this.listen(window, 'scroll', this.handleScroll, { passive: true });
      this.listen(document, 'click', this.handleDocumentClick);
      this.listen(document, 'keydown', this.handleDocumentKeydown);
      this.listen(document, 'ao:header:reveal', () => this.show(true));
      this.listen(document, 'ao:cart:opening', () => this.closeDrawer(false));

      this.listen(document, 'focusin', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest(ANNOUNCEMENT_SELECTOR)) return;

        this.updateAnnouncementVisibility(this.getCurrentScrollY(), true);
        this.show(true);
      });

      this.listen(document, 'focusout', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest(ANNOUNCEMENT_SELECTOR)) return;

        requestAnimationFrame(() => {
          if (this.destroyed || document.activeElement?.closest(ANNOUNCEMENT_SELECTOR)) return;

          this.updateAnnouncementVisibility(this.getCurrentScrollY());
        });
      });

      this.listen(this.root, 'pointerdown', () => {
        this.interactionUntil = Date.now() + 1200;
        this.show(true);
      });

      this.listen(this.root, 'focusin', () => this.show(true));
      this.listen(this.root, 'focusout', () => {
        requestAnimationFrame(() => {
          if (this.destroyed) return;

          this.lastScrollY = this.getCurrentScrollY();
          this.resetDirectionState();
        });
      });

      this.listen(this.openButton, 'click', () => this.openDrawer());

      this.closeButtons.forEach((button) => {
        this.listen(button, 'click', () => this.closeDrawer(true));
      });

      this.details.forEach((detail) => {
        this.listen(detail, 'toggle', () => {
          if (detail.open) {
            this.show(true);
          } else {
            this.lastScrollY = Math.max(window.scrollY || 0, 0);
            this.resetDirectionState();
          }
        });
      });
    }

    observeCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');

      if (!cartDrawer || typeof MutationObserver === 'undefined') return;

      this.cartDrawerObserver = new MutationObserver(() => {
        if (this.isCartDrawerOpen()) {
          this.show(true);
        }
      });

      this.cartDrawerObserver.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class', 'open', 'aria-hidden'],
      });
    }

    handleScroll() {
      if (this.frame !== null) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateFromScroll();
      });
    }

    updateFromScroll() {
      const currentScrollY = this.getCurrentScrollY();
      const delta = currentScrollY - this.lastScrollY;
      const now = Date.now();
      this.lastScrollY = currentScrollY;

      this.root.classList.toggle('is-scrolled', currentScrollY > 8);
      const announcementFocused = Boolean(document.activeElement?.closest(ANNOUNCEMENT_SELECTOR));
      const announcementHeight = this.updateAnnouncementVisibility(currentScrollY, announcementFocused);

      if (this.isInteractionLocked() || currentScrollY <= this.topZone) {
        this.show(true);
        return;
      }

      if (Math.abs(delta) <= this.noiseTolerance) return;

      const nextDirection = delta > 0 ? 1 : -1;

      if (nextDirection !== this.direction || now - this.lastMovementAt > this.directionWindow) {
        this.direction = nextDirection;
        this.directionTravel = 0;
      }

      this.lastMovementAt = now;
      this.directionTravel += Math.abs(delta);
      const hideStart = this.mobileViewport.matches
        ? Math.max(this.hideStart, announcementHeight)
        : this.hideStart;

      if (
        this.direction > 0 &&
        currentScrollY >= hideStart &&
        this.directionTravel >= this.downTolerance
      ) {
        this.hide();
        this.directionTravel = 0;
      } else if (this.direction < 0 && this.directionTravel >= this.upTolerance) {
        this.show();
        this.directionTravel = 0;
      }
    }

    isInteractionLocked() {
      return (
        Boolean(window.Shopify && window.Shopify.designMode) ||
        this.isDrawerOpen() ||
        this.isCartDrawerOpen() ||
        Date.now() < this.interactionUntil ||
        this.root.contains(document.activeElement) ||
        Boolean(document.activeElement?.closest(ANNOUNCEMENT_SELECTOR)) ||
        Boolean(this.root.querySelector('details[open]')) ||
        Boolean(this.root.querySelector('[aria-expanded="true"]'))
      );
    }

    isDrawerOpen() {
      return Boolean(this.drawer && !this.drawer.hidden);
    }

    getCurrentScrollY() {
      if (this.isDrawerOpen() && Number.isFinite(this.drawerScrollY)) {
        return this.drawerScrollY;
      }

      return Math.max(window.scrollY || 0, 0);
    }

    isCartDrawerOpen() {
      const cartDrawer = document.querySelector('cart-drawer');

      return Boolean(
        cartDrawer &&
          (cartDrawer.classList.contains('active') ||
            cartDrawer.hasAttribute('open') ||
            cartDrawer.getAttribute('aria-hidden') === 'false'),
      );
    }

    show(resetDirection = false) {
      document.documentElement.classList.remove(SMART_HEADER_CLASS);
      this.root.classList.remove('is-hidden');
      this.hidden = false;

      if (resetDirection) {
        this.resetDirectionState();
      }
    }

    hide() {
      if (this.hidden || this.isInteractionLocked()) return;

      document.documentElement.classList.add(SMART_HEADER_CLASS);
      this.root.classList.add('is-hidden');
      this.hidden = true;
    }

    resetDirectionState() {
      this.direction = 0;
      this.directionTravel = 0;
      this.lastMovementAt = 0;
    }

    updateAnnouncementVisibility(scrollY, keepVisible = false) {
      const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      const announcement = document.querySelector(ANNOUNCEMENT_SELECTOR);
      const announcementHeight = announcement
        ? announcementHeightCache || Math.ceil(announcement.getBoundingClientRect().height)
        : 0;
      const documentElement = document.documentElement;
      const documentStyle = documentElement.style;

      if (isDesignMode) {
        documentElement.classList.remove(ANNOUNCEMENT_HIDDEN_CLASS);
        documentStyle.setProperty('--ao-announcement-mobile-offset', '0px');
        documentStyle.setProperty('--ao-header-mobile-sticky-top', `${announcementHeight}px`);
        return announcementHeight;
      }

      if (keepVisible && announcementHeight > 0) {
        documentElement.classList.remove(ANNOUNCEMENT_HIDDEN_CLASS);
        documentStyle.setProperty('--ao-announcement-mobile-offset', '0px');
        documentStyle.setProperty('--ao-header-mobile-sticky-top', `${announcementHeight}px`);
        return announcementHeight;
      }

      if (this.mobileViewport.matches && announcementHeight > 0) {
        const announcementTravel = Math.min(scrollY, announcementHeight);

        documentStyle.setProperty('--ao-announcement-mobile-offset', `${-announcementTravel}px`);
        documentStyle.setProperty(
          '--ao-header-mobile-sticky-top',
          `${Math.max(announcementHeight - announcementTravel, 0)}px`,
        );
        documentElement.classList.toggle(ANNOUNCEMENT_HIDDEN_CLASS, scrollY >= announcementHeight);
        return announcementHeight;
      }

      documentStyle.removeProperty('--ao-announcement-mobile-offset');
      documentStyle.removeProperty('--ao-header-mobile-sticky-top');
      documentElement.classList.toggle(ANNOUNCEMENT_HIDDEN_CLASS, scrollY > this.topZone);
      return announcementHeight;
    }

    restoreVisibility() {
      this.lastScrollY = this.getCurrentScrollY();
      this.root.classList.toggle('is-scrolled', this.lastScrollY > 8);
      this.updateAnnouncementVisibility(this.lastScrollY);
      this.show(true);
    }

    openDrawer() {
      if (!this.drawer) return;

      const reopening = this.drawer.classList.contains('is-closing');

      if (reopening) {
        this.cancelDrawerClose();
      } else if (this.isDrawerOpen()) {
        return;
      }

      if (!reopening) {
        this.drawerScrollY = Math.max(window.scrollY || 0, 0);
        this.previousFocus = document.activeElement;
      }

      this.show(true);
      this.pendingDrawerRestoreFocus = false;
      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.style.setProperty('--ao-head-scroll-lock-top', `${-this.drawerScrollY}px`);
      document.body.classList.add('ao-head-no-scroll');

      if (this.drawerOpenFrame !== null) cancelAnimationFrame(this.drawerOpenFrame);
      this.drawerOpenFrame = requestAnimationFrame(() => {
        this.drawerOpenFrame = null;
        if (this.destroyed) return;

        this.drawer.classList.add('is-open');
        const closeControl = this.drawer.querySelector('button[data-ao2-menu-close]');
        const firstFocusable = this.getDrawerFocusable()[0];
        (closeControl || firstFocusable)?.focus({ preventScroll: true });
      });
    }

    closeDrawer(restoreFocus = false, immediate = false) {
      if (!this.drawer || this.drawer.hidden) return;

      this.pendingDrawerRestoreFocus = this.pendingDrawerRestoreFocus || restoreFocus;

      if (this.drawerOpenFrame !== null) {
        cancelAnimationFrame(this.drawerOpenFrame);
        this.drawerOpenFrame = null;
      }

      if (this.drawer.classList.contains('is-closing')) return;

      this.drawer.classList.remove('is-open');
      this.drawer.classList.add('is-closing');
      this.openButton?.setAttribute('aria-expanded', 'false');

      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.finishDrawerClose();
        return;
      }

      const panel = this.drawer.querySelector('.ao-head-drawer-panel');

      if (!panel) {
        this.finishDrawerClose();
        return;
      }

      this.drawerTransitionTarget = panel;
      this.drawerTransitionHandler = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;

        this.finishDrawerClose();
      };
      panel.addEventListener('transitionend', this.drawerTransitionHandler);
      this.drawerCloseTimer = window.setTimeout(() => this.finishDrawerClose(), 500);
    }

    finishDrawerClose() {
      if (!this.drawer || this.drawer.hidden) return;

      const restoreFocus = this.pendingDrawerRestoreFocus;
      const restoreScrollY = Number.isFinite(this.drawerScrollY)
        ? this.drawerScrollY
        : Math.max(window.scrollY || 0, 0);

      this.cancelDrawerClose();
      this.drawer.hidden = true;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ao-head-no-scroll');
      document.body.style.removeProperty('--ao-head-scroll-lock-top');

      window.scrollTo(0, restoreScrollY);

      this.drawerScrollY = null;
      this.lastScrollY = restoreScrollY;
      this.root.classList.toggle('is-scrolled', restoreScrollY > 8);
      this.updateAnnouncementVisibility(restoreScrollY);
      this.resetDirectionState();
      this.show();

      if (restoreFocus && this.previousFocus instanceof HTMLElement && this.previousFocus.isConnected) {
        this.previousFocus.focus({ preventScroll: true });
      }

      this.previousFocus = null;
      this.pendingDrawerRestoreFocus = false;
    }

    cancelDrawerClose() {
      if (this.drawerCloseTimer !== null) {
        window.clearTimeout(this.drawerCloseTimer);
        this.drawerCloseTimer = null;
      }

      if (this.drawerTransitionTarget && this.drawerTransitionHandler) {
        this.drawerTransitionTarget.removeEventListener('transitionend', this.drawerTransitionHandler);
      }

      this.drawerTransitionTarget = null;
      this.drawerTransitionHandler = null;
      this.drawer?.classList.remove('is-closing');
    }

    getDrawerFocusable() {
      if (!this.drawer) return [];

      return [...this.drawer.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0,
      );
    }

    trapDrawerFocus(event) {
      const focusable = this.getDrawerFocusable();

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!this.drawer.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape') {
        const drawerWasOpen = this.isDrawerOpen();

        if (drawerWasOpen) {
          event.preventDefault();
          this.closeDrawer(true);
        }

        let closedDetails = false;
        this.details.forEach((detail) => {
          if (detail.open) {
            detail.removeAttribute('open');
            closedDetails = true;
          }
        });

        if (drawerWasOpen || closedDetails) {
          this.show(true);
        }
      } else if (event.key === 'Tab' && this.isDrawerOpen()) {
        this.trapDrawerFocus(event);
      }
    }

    handleDocumentClick(event) {
      this.details.forEach((detail) => {
        if (detail.open && !detail.contains(event.target)) {
          detail.removeAttribute('open');
        }
      });
    }

    destroy() {
      this.destroyed = true;

      if (this.frame !== null) {
        cancelAnimationFrame(this.frame);
      }

      if (this.drawerOpenFrame !== null) {
        cancelAnimationFrame(this.drawerOpenFrame);
        this.drawerOpenFrame = null;
      }

      this.listeners.forEach((removeListener) => removeListener());
      this.cartDrawerObserver?.disconnect();
      this.closeDrawer(false, true);
      document.documentElement.classList.remove(SMART_HEADER_CLASS);
      document.documentElement.classList.remove(ANNOUNCEMENT_HIDDEN_CLASS);
      document.documentElement.style.removeProperty('--ao-announcement-mobile-offset');
      document.documentElement.style.removeProperty('--ao-header-mobile-sticky-top');
    }
  }

  const runtime = {
    announcements: new Map(),
    headers: new Map(),
    geometryObserver: null,
    geometryFrame: null,
    geometryNeedsRefresh: false,
    listeners: [],

    listen(target, eventName, handler, options) {
      target.addEventListener(eventName, handler, options);
      this.listeners.push(() => target.removeEventListener(eventName, handler, options));
    },

    init(scope = document) {
      this.pruneDisconnected();

      getMatches(scope, ANNOUNCEMENT_SELECTOR).forEach((root) => {
        if (!this.announcements.has(root)) {
          this.announcements.set(root, new AnnouncementRotator(root));
        }
      });

      getMatches(scope, HEADER_SELECTOR).forEach((root) => {
        if (!this.headers.has(root)) {
          this.headers.set(root, new SmartHeader(root));
        }
      });

      this.observeGeometry();
      this.syncGeometry();
      this.queueGeometrySync();
      document.documentElement.classList.add(SMART_HEADER_READY_CLASS);
    },

    pruneDisconnected() {
      this.announcements.forEach((instance, root) => {
        if (!root.isConnected) {
          instance.destroy();
          this.announcements.delete(root);
        }
      });

      this.headers.forEach((instance, root) => {
        if (!root.isConnected) {
          instance.destroy();
          this.headers.delete(root);
        }
      });
    },

    destroyWithin(scope) {
      this.announcements.forEach((instance, root) => {
        if (scope === root || (scope instanceof Element && scope.contains(root))) {
          instance.destroy();
          this.announcements.delete(root);
        }
      });

      this.headers.forEach((instance, root) => {
        if (scope === root || (scope instanceof Element && scope.contains(root))) {
          instance.destroy();
          this.headers.delete(root);
        }
      });

      this.geometryObserver?.disconnect();
      this.queueGeometrySync(true);
    },

    observeGeometry() {
      if (typeof ResizeObserver === 'undefined') return;

      if (!this.geometryObserver) {
        this.geometryObserver = new ResizeObserver(() => this.queueGeometrySync());
      }

      this.geometryObserver.disconnect();

      const announcement = document.querySelector(ANNOUNCEMENT_SELECTOR);
      const header = document.querySelector(HEADER_SELECTOR);

      if (announcement) this.geometryObserver.observe(announcement);
      if (header) this.geometryObserver.observe(header);
    },

    queueGeometrySync(refreshObserver = false) {
      if (refreshObserver) {
        this.geometryNeedsRefresh = true;
      }

      if (this.geometryFrame !== null) return;

      this.geometryFrame = requestAnimationFrame(() => {
        this.geometryFrame = null;

        if (this.geometryNeedsRefresh) {
          this.geometryNeedsRefresh = false;
          this.observeGeometry();
        }

        this.syncGeometry();
      });
    },

    syncGeometry() {
      const announcement = document.querySelector(ANNOUNCEMENT_SELECTOR);
      const header = document.querySelector(HEADER_SELECTOR);
      const announcementHeight = announcement ? Math.ceil(announcement.getBoundingClientRect().height) : 0;
      const headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      const announcementBeforeHeader = Boolean(
        announcement &&
          header &&
          (announcement.compareDocumentPosition(header) & Node.DOCUMENT_POSITION_FOLLOWING),
      );
      const headerStickyTop = announcementBeforeHeader ? announcementHeight : 0;
      const announcementStickyTop = announcement && header && !announcementBeforeHeader ? headerHeight : 0;
      const headerHideOffset = -headerHeight;
      const announcementHideOffset = announcement && header && !announcementBeforeHeader
        ? -(headerHeight + announcementHeight)
        : -announcementHeight;
      const documentStyle = document.documentElement.style;

      announcementHeightCache = announcementHeight;

      documentStyle.setProperty('--ao-announcement-height', `${announcementHeight}px`);
      documentStyle.setProperty('--ao-header-height', `${headerHeight}px`);
      documentStyle.setProperty('--ao-header-sticky-top', `${headerStickyTop}px`);
      documentStyle.setProperty('--ao-announcement-sticky-top', `${announcementStickyTop}px`);
      documentStyle.setProperty('--ao-header-hide-offset', `${headerHideOffset}px`);
      documentStyle.setProperty('--ao-announcement-hide-offset', `${announcementHideOffset}px`);

      if (window.matchMedia('(max-width: 900px)').matches) {
        const announcementTravel = Math.min(Math.max(window.scrollY || 0, 0), announcementHeight);
        documentStyle.setProperty('--ao-announcement-mobile-offset', `${-announcementTravel}px`);
        documentStyle.setProperty(
          '--ao-header-mobile-sticky-top',
          `${Math.max(headerStickyTop - announcementTravel, 0)}px`,
        );
      } else {
        documentStyle.removeProperty('--ao-announcement-mobile-offset');
        documentStyle.removeProperty('--ao-header-mobile-sticky-top');
      }
    },

    handleSectionLoad(event) {
      this.init(event.target);
    },

    handleSectionUnload(event) {
      this.destroyWithin(event.target);
    },

    handleSectionReorder() {
      this.queueGeometrySync(true);
    },

    handlePageShow() {
      this.headers.forEach((instance) => instance.restoreVisibility());
      this.queueGeometrySync(true);
    },

    handleBlockSelection(event, selected) {
      const blockId = event.detail && event.detail.blockId;

      if (!blockId) return;

      this.announcements.forEach((instance) => instance.selectBlock(blockId, selected));
    },

    start() {
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handleSectionReorder = this.handleSectionReorder.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.handleBlockSelect = (event) => this.handleBlockSelection(event, true);
      this.handleBlockDeselect = (event) => this.handleBlockSelection(event, false);
      this.handleResize = () => this.queueGeometrySync();

      this.listen(document, 'shopify:section:load', this.handleSectionLoad);
      this.listen(document, 'shopify:section:unload', this.handleSectionUnload);
      this.listen(document, 'shopify:section:reorder', this.handleSectionReorder);
      this.listen(document, 'shopify:block:select', this.handleBlockSelect);
      this.listen(document, 'shopify:block:deselect', this.handleBlockDeselect);
      this.listen(window, 'resize', this.handleResize, { passive: true });
      this.listen(window, 'pageshow', this.handlePageShow);

      if (document.readyState === 'loading') {
        this.handleDomReady = () => this.init(document);
        this.listen(document, 'DOMContentLoaded', this.handleDomReady, { once: true });
      } else {
        this.init(document);
      }
    },

    destroy() {
      this.announcements.forEach((instance) => instance.destroy());
      this.headers.forEach((instance) => instance.destroy());
      this.announcements.clear();
      this.headers.clear();
      this.listeners.forEach((removeListener) => removeListener());
      this.geometryObserver?.disconnect();

      if (this.geometryFrame !== null) {
        cancelAnimationFrame(this.geometryFrame);
      }

      document.documentElement.classList.remove(SMART_HEADER_CLASS, SMART_HEADER_READY_CLASS);
      document.documentElement.style.removeProperty('--ao-announcement-height');
      document.documentElement.style.removeProperty('--ao-header-height');
      document.documentElement.style.removeProperty('--ao-header-sticky-top');
      document.documentElement.style.removeProperty('--ao-announcement-sticky-top');
      document.documentElement.style.removeProperty('--ao-header-hide-offset');
      document.documentElement.style.removeProperty('--ao-announcement-hide-offset');
      document.documentElement.style.removeProperty('--ao-announcement-mobile-offset');
      document.documentElement.style.removeProperty('--ao-header-mobile-sticky-top');
    },
  };

  window.__aoHeaderRuntime = runtime;
  runtime.start();
})();
